import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { Center, Spinner } from 'src/components/atoms';
import { PaginatedTableSearch } from 'src/components/atomsLegacy';
import { CaseDataDownloadButton, EmptyState } from 'src/components/molecules';
import { CaseCreateNewButton } from 'src/components/molecules/CaseCreateNewButton/CaseCreateNewButton';
import {
  DropdownOptions,
  FilterSelect,
} from 'src/components/molecules/FilterSelect/FilterSelect';
import { SelectDropdownGeneric } from 'src/components/moleculesLegacy';
import { PaginatedTable } from 'src/components/organisms';
import { AppliedFiltersType } from 'src/graphql';
import {
  useCalculateTableRows,
  useCaseSearchQueryParams,
  useIsFeatureEnabled,
  usePageQueryParams,
  useRedirect,
} from 'src/hooks';
import { FeatureFlag, FormState } from 'src/types';
import * as Types from '../../../types';
import {
  ActionsWrapper,
  EmptyStateContainer,
  PageContainer,
  TableActions,
  TableContainer,
  TableHeader,
} from './Cases.styles';
import { SpeakUpCases } from './SpeakUpCases';
import { useCaseTable } from './useCaseTable';

const PADDING = 24;
const ACTIONS_ROW = 40;
const MARGIN = 20;
const TABLE_HEADER = 56;
const ROW_HEIGHT = 56;
const MIN_ROWS = 7;
const PAGINATION_CONTAINER = 56;
const PAGINATION_PADDING = 6;
const PAGE_SPACE_EXCLUDING_ROWS =
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN + 2 * PAGINATION_PADDING;
const SCROLLBAR_HEIGHT = 16;
const MIN_TABLE_HEIGHT =
  MIN_ROWS * ROW_HEIGHT +
  TABLE_HEADER +
  SCROLLBAR_HEIGHT +
  PAGINATION_CONTAINER +
  2 * PAGINATION_PADDING;

const tableHeight = `calc(100vh - ${
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN - PAGINATION_CONTAINER
}px)`;

const MAX_COLUMN_WIDTH = 200;
const FULL_WIDTH_THRESHOLD = 2200;

export const Cases = () => {
  const { showRedirect, redirectTo, redirect } = useRedirect();
  const caseDataDownloadFeatureFlagEnabled = useIsFeatureEnabled(
    FeatureFlag.CASE_DATA_DOWNLOAD,
  );
  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );
  const caseLinkingEnabled = useIsFeatureEnabled(FeatureFlag.LINKING_CASES);
  const { page, setPage, resetParams } = usePageQueryParams('/cases');
  const { rows } = useCalculateTableRows(
    ROW_HEIGHT,
    MIN_ROWS,
    PAGE_SPACE_EXCLUDING_ROWS,
  );

  const [numPages, setNumPages] = useState(page ?? 1);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersType>({});

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const {
    caseQueryParamFields,
    paramSearchValues,
    setCaseQueryParamFields,
    updateUrlParams,
  } = useCaseSearchQueryParams(queryParams);

  const {
    columns,
    sortableColumns,
    visibleColumns,
    columnInfoTooltips,
    updateVisibleColumns,
    tableData,
    isLoading,
    isError,
    caseSearchOptions,
    searchCases,
    addNewReport,
    addExistingReport,
    sortColumns,
    openReport,
    sortedColumn,
    count,
    searchValue,
  } = useCaseTable(
    rows,
    page,
    redirect,
    appliedFilters,
    caseQueryParamFields,
    caseLinkingEnabled,
  );

  const currentUser = useSelector(
    (state: Types.State) => state.globalUsers.currentUser,
  );
  const hasListAllCases =
    currentUser?.systemPermissions?.includes('LIST_ALL_CASES');
  const caseDataDownloadEnabled =
    hasListAllCases && caseDataDownloadFeatureFlagEnabled;

  // Reset page number on error
  useEffect(() => {
    if (isError) {
      setPage(1);
    }
  }, [setPage, isError]);

  // Update number of pages when table data or rows change
  useEffect(() => {
    if (count) {
      setNumPages(Math.ceil(count / rows));
    }
  }, [count, rows]);

  // Set page to highest page number if greater than max
  useEffect(() => {
    if (Number(page) > numPages) {
      setPage(numPages);
    }
  }, [numPages, page, setPage]);

  const filterOptions = [
    {
      value: 'caseLead',
      label: 'Case lead',
      checked: appliedFilters.caseLead ?? false,
    },
    {
      value: 'teamMember',
      label: 'Team member',
      checked: appliedFilters.teamMember ?? false,
    },
  ];

  const handleFilterChange = (selected: DropdownOptions[]) => {
    const values = selected
      ? selected.reduce((acc, curr) => {
          return { ...acc, [String(curr.value)]: curr.checked };
        }, {})
      : {};
    setAppliedFilters(values as AppliedFiltersType);
  };

  const [isFullWidth, setIsFullWidth] = useState(
    window.innerWidth > FULL_WIDTH_THRESHOLD,
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const currentWidth = window.innerWidth;
      setIsFullWidth(currentWidth > FULL_WIDTH_THRESHOLD);
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const actualRowCount = tableData?.length || 0;

  const maxRowsToDisplay =
    actualRowCount < rows
      ? Math.max(actualRowCount, MIN_ROWS)
      : rows < MIN_ROWS
        ? MIN_ROWS
        : rows;

  const shouldUseFullHeight = actualRowCount >= rows;

  if (!isLegacyMode) {
    return <SpeakUpCases />;
  }

  return (
    <PageContainer className="routing-body-content" data-testid="cases-page">
      {showRedirect && (
        <Redirect data-testid={redirectTo} to={`${redirectTo}`} push />
      )}
      <TableHeader>
        <TableActions>
          <PaginatedTableSearch
            options={caseSearchOptions}
            applyFilter={searchCases}
            paramSearchValues={paramSearchValues}
            setCaseQueryParamFields={setCaseQueryParamFields}
            resetParams={resetParams}
            updateUrlParams={updateUrlParams}
          />
          <SelectDropdownGeneric
            options={columns(caseLinkingEnabled).map((visibleCol) => ({
              label: visibleCol.label,
              value: visibleCol.value ?? '',
            }))}
            withBorder={false}
            isMulti={true}
            defaultValue={visibleColumns.map((visibleCol) => ({
              label: visibleCol.label,
              value: visibleCol.value ?? '',
            }))}
            label={'Show/Hide columns'}
            customStyle={{
              controlStyle: {
                backgroundColor: 'transparent',
                width: '172px',
              },
              valueContainer: {
                paddingLeft: '0',
                paddingRight: '0',
                fontSize: '15px',
              },
              dropdownIndicator: {
                paddingLeft: '0',
                paddingRight: '0',
                marginRight: '8px',
              },
            }}
            onChange={updateVisibleColumns}
            dataQa="show-hide-columns-button"
            classNamePrefix="select"
            zIndex={12}
          />
        </TableActions>
        <ActionsWrapper>
          {caseDataDownloadEnabled && <CaseDataDownloadButton />}
          <FilterSelect
            state={FormState.DEFAULT}
            options={filterOptions}
            size="medium"
            onChange={(selected) => handleFilterChange(selected)}
            $zIndex={12}
          />
          <CaseCreateNewButton
            addNewReport={addNewReport}
            addExistingReport={addExistingReport}
          />
        </ActionsWrapper>
      </TableHeader>
      {isLoading ? (
        <Center>
          <Spinner variant="secondary" size="large" />
        </Center>
      ) : (
        <TableContainer>
          <PaginatedTable
            sortableColumns={sortableColumns}
            minHeight={MIN_TABLE_HEIGHT}
            height={tableHeight}
            tableData={tableData}
            columns={columns(caseLinkingEnabled)}
            onColumnSort={sortColumns}
            data-testid="cases-table"
            maxRows={maxRowsToDisplay}
            rowHeight={ROW_HEIGHT}
            isScrollable={true}
            fixedRowHeight={true}
            sortedColumn={sortedColumn}
            numPages={numPages}
            isFullHeight={shouldUseFullHeight}
            isFullWidth={isFullWidth}
            showPagination
            handleClickPage={(index: number) => setPage(index)}
            onRowClick={openReport}
            borderRadius="12px"
            paginationPadding="24px 0px"
            customEmptyState={
              <EmptyStateContainer>
                <EmptyState
                  data-testid="cases-table-empty-state"
                  size="default"
                  buttonText="Add new case"
                  title={
                    searchValue ? 'No results found' : 'No cases available'
                  }
                  description={
                    searchValue
                      ? 'Review your filters or refine your search to view more results.'
                      : 'You have currently no cases in the system.'
                  }
                  renderIconButton={true}
                  onButtonClick={searchValue ? undefined : addNewReport}
                />
              </EmptyStateContainer>
            }
            visibleColumns={visibleColumns.map((column) => {
              return {
                label: column.label,
                value: column.value,
                showOverflow: column.showOverflow,
              };
            })}
            currentPage={page}
            emptyText={
              searchValue ? 'No cases found.' : `You don't have any cases.`
            }
            isAlreadyPaginated
            columnInfoTooltips={columnInfoTooltips}
            maxColumnWidth={`${MAX_COLUMN_WIDTH}px`}
          />
        </TableContainer>
      )}
    </PageContainer>
  );
};
